import { Link } from 'react-router-dom';
import { Separator } from '@givebutter/primitives/separator';
import {
    faBank,
    faFlag,
    faFlagCheckered,
    faHandHoldingDollar,
    faTimes,
    faUserLock,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Dialog from 'components/dialog';
import * as Goalbar from 'components/goalbar';
import { CreateFirstCampaignChecklistItem } from 'components/onboarding-checklist/CreateFirstCampaignChecklistItem';
import { OnboardingChecklistGuides } from 'components/onboarding-checklist/onboarding-checklist-guides';
import * as ChecklistItem from 'components/onboarding-checklist/onboarding-checklist-item';
import { OnboardingJumbo } from 'components/onboarding-checklist/onboarding-jumbo';
import { Stickers } from 'components/onboarding-checklist/stickers';
import { useChecklist } from 'components/onboarding-checklist/useChecklist';
import { NoPointerEvents } from 'components/util';
import { VerifyBankModalContent } from 'components/verify-bank';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import ButterDrip from 'images/butter-drip.svg?react';

const OnboardingChecklist = () => {
    const { accountId } = useCurrentAccount();
    const {
        jumboTitle,
        jumboBody,
        showJumboParty,
        showGoalbar,
        goalbarAmount,
        showVerifyAccountChecklistItem,
        isVerifyAccountDisabled,
        isVerifyAccountCompleted,
        accountChecklistText,
        showVerifyBankChecklistItem,
        isVerifyBankDisabled,
        isVerifyBankCompleted,
        showVerifyCampaignChecklistItem,
        isVerifyCampaignDisabled,
        isVerifyCampaignCompleted,
        showFirstDollarChecklistItem,
        isFirstDollarCompleted,
        showChecklistGuides,
        percentageOutOfMax,
        percentComplete,
        showMaxRaisedModal,
        setShowMaxRaisedModal,
    } = useChecklist();

    return (
        <>
            <OnboardingJumbo
                title={jumboTitle}
                body={jumboBody}
                showParty={showJumboParty}
                percentComplete={percentComplete}
                showGoalBar={showGoalbar}
            />
            {showGoalbar && (
                <div className={'p-8'}>
                    <div className="text-base font-medium">{goalbarAmount}</div>
                    <Goalbar.Root percentage={percentageOutOfMax}>
                        <Goalbar.Indicator />
                        <FontAwesomeIcon
                            icon={faFlagCheckered}
                            className="absolute right-0 top-[-1.2rem]"
                        />
                    </Goalbar.Root>
                </div>
            )}
            <div className={'relative'}>
                <Separator className={'bg-brand-butter-primary !h-1.5'} />
                <ButterDrip className={'pointer-events-none absolute right-1/4 top-px z-10'} />
            </div>
            <div className={'flex flex-col'}>
                {showVerifyAccountChecklistItem && (
                    <>
                        <NoPointerEvents disabled={isVerifyAccountDisabled}>
                            <Link to={`/accounts/${accountId}/settings/about/verify`}>
                                <ChecklistItem.Root
                                    icon={<FontAwesomeIcon icon={faUserLock} />}
                                    isCompleted={isVerifyAccountCompleted}
                                >
                                    <ChecklistItem.Text className="text-xl">
                                        {accountChecklistText}
                                    </ChecklistItem.Text>
                                </ChecklistItem.Root>
                            </Link>
                        </NoPointerEvents>
                        <Separator />
                    </>
                )}
                {showVerifyBankChecklistItem && (
                    <>
                        <NoPointerEvents disabled={isVerifyBankDisabled}>
                            <Dialog.Root>
                                <Dialog.Trigger asChild={true}>
                                    <div>
                                        <ChecklistItem.Root
                                            icon={<FontAwesomeIcon icon={faBank} />}
                                            isCompleted={isVerifyBankCompleted}
                                        >
                                            <ChecklistItem.Text className="text-xl">
                                                Connect your payout method
                                            </ChecklistItem.Text>
                                        </ChecklistItem.Root>
                                    </div>
                                </Dialog.Trigger>
                                <Dialog.Portal>
                                    <Dialog.Overlay />
                                    <VerifyBankModalContent />
                                </Dialog.Portal>
                            </Dialog.Root>
                        </NoPointerEvents>
                        <Separator />
                    </>
                )}
                {showVerifyCampaignChecklistItem && (
                    <>
                        <NoPointerEvents disabled={isVerifyCampaignDisabled}>
                            <CreateFirstCampaignChecklistItem
                                icon={<FontAwesomeIcon icon={faFlag} />}
                                isCompleted={isVerifyCampaignCompleted}
                            />
                        </NoPointerEvents>
                        <Separator />
                    </>
                )}
                {showFirstDollarChecklistItem && (
                    <>
                        <ChecklistItem.Root
                            icon={<FontAwesomeIcon icon={faHandHoldingDollar} />}
                            interactive={false}
                            isCompleted={isFirstDollarCompleted}
                        >
                            <ChecklistItem.Text className="text-xl">
                                Raise your first dollar
                            </ChecklistItem.Text>
                        </ChecklistItem.Root>
                        <Separator />
                    </>
                )}
            </div>
            {showChecklistGuides && <OnboardingChecklistGuides />}
            <Dialog.Root
                open={showMaxRaisedModal}
                onOpenChange={isOpen => setShowMaxRaisedModal(isOpen)}
            >
                <Dialog.Portal>
                    <Dialog.Overlay />
                    <Dialog.Content className="max-w-[25.5rem]">
                        <Stickers />
                        <Dialog.Close>
                            <FontAwesomeIcon icon={faTimes} />
                        </Dialog.Close>
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog.Root>
        </>
    );
};

export { OnboardingChecklist };
